import { useCallback, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { bridge, isNotNil } from 'shared/src/utils';
import { useLogAnalyticsEvent } from 'shared/src/hooks';
import type { GoodsData } from '@banhala/ui-kit';
import type { GoodsLikeRecommendItem } from 'shared/src/api';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';

type RecommendGoodsItem = GoodsData & GoodsLikeRecommendItem;

// 검색 보조 화면 "최근 본 검색 상품" 캐로셀 노출 로깅 (SCREEN=SEARCH 고정).
// iOS: SearchHelperFeature 추천 캐로셀 display / Android: SearchSectionScreen 노출 로깅 대응.
// PDP 의 useLogDisplayGoods 를 SCREEN 고정 형태로 검색 도메인에 분리.
export const useLogDisplayRecommendGoods = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const loggedIndices = useRef<Array<number>>([]);
  const pendingItems = useRef<Array<{ index: number; item: RecommendGoodsItem }>>(
    [],
  );

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const logItem = useCallback(
    (index: number, item: RecommendGoodsItem) => {
      logAnalyticsEvent({
        name: 'DISPLAY_GOODS',
        ably_only_log: item.logging.ably_only_log,
        analytics: {
          ...item.logging.analytics,
          INDEX: index,
        },
      });
      if (isNotNil(item.item.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
      }
    },
    [logAnalyticsEvent],
  );

  // enabled 가 뒤늦게 켜지면 대기 중이던 노출을 일괄 발화 (캐로셀 게이팅 대응)
  useEffect(() => {
    if (enabled && pendingItems.current.length > 0) {
      pendingItems.current.forEach(({ index, item }) => {
        if (loggedIndices.current.includes(index)) return;
        loggedIndices.current.push(index);
        logItem(index, item);
      });
      pendingItems.current = [];
    }
  }, [enabled, logItem]);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<RecommendGoodsItem>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (loggedIndices.current.includes(index)) return;

        if (!enabledRef.current) {
          if (!pendingItems.current.some(p => p.index === index)) {
            pendingItems.current.push({ index, item });
          }
          return;
        }

        loggedIndices.current.push(index);
        logItem(index, item);
      });
    },
    [logItem],
  );

  return { logDisplayGoods };
};
